import { Component, OnInit } from '@angular/core';
import { AssessmentSummary } from '../../../models/assessment-summary';
import { AgilityRiskService } from '../../../providers/agility-risk.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { AssessmentComment } from '../../../models/assessment-comment';
import { DialogService } from 'primeng/dynamicdialog';
import { AssessmentDetailAdditionalCommentsModalComponent } from './modals/assessment-detail-additional-comments-modal/assessment-detail-additional-comments-modal.component';
import { AnswerTemplate, RiskDetail } from '../../../models/risk-detail';
import { BaseResponse } from '../../../models/base-response';
import { RiskBand } from '../../../models/risk-band';
import { DynamicDialogRef } from 'primeng/dynamicdialog/dynamicdialog-ref';
import { RiskAssessmentRiskMemberAnswersModalComponent } from './modals/risk-assessment-risk-member-answers-modal/risk-assessment-risk-member-answers-modal.component';
import { RiskAssessmentDetailsCustomFieldsModalComponent } from './modals/risk-assessment-details-custom-fields-modal/risk-assessment-details-custom-fields-modal.component';
import { AssessmentStatus } from '../../../models/assessment-status';
import { AgilityRiskMessageService } from '../../../providers/agility-risk-message.service';
import { AssessmentDistributionMemberData } from '../../../models/assessment-distribution-member-data';
import { AgilityRiskPermissionService } from '../../../providers/agility-risk-permission.service';
import { GetRiskAssessmentSummaryResponse } from '../../../models/get-risk-assessment-summary-response';
import { AgilityRiskNavigationService } from '../../../providers/agility-risk-navigation.service';

@Component({
  selector: 'lib-risk-assessment-details',
  templateUrl: './risk-assessment-details.component.html',
  styleUrls: ['./risk-assessment-details.component.scss']
})
export class RiskAssessmentDetailsComponent implements OnInit {
  public threatsHorizontalChartData: any;
  public threatsHorizontalChartOptions: any;
  public riskAssessmentSummaries: Array<AssessmentSummary> = [];
  public annotations = [];
  public assessmentId: string;
  public activeRiskDetail: RiskDetail;
  public options = [
    {
      label: this.translateService.instant('common.alphabetical'),
      value: this.translateService.instant('common.name').toLowerCase()
    },
    {
      label: this.translateService.instant('assessment.survey.riskBands'),
      value: this.translateService.instant('common.rank').toLowerCase()
    }
  ];
  public chartSortBy: string = this.translateService.instant('common.alphabetical');
  public additionalComments: Array<AssessmentComment> = [];
  public assessmentSummary: AssessmentSummary;
  public assessmentSummaryClone: AssessmentSummary; // store as backup
  public riskBands: Array<RiskBand> = [];
  public activeRiskDetailId: any;
  public assessmentStatus = AssessmentStatus;
  public initializingChart = true;
  public chartHasData = false;
  public isEditingAssessmentName: boolean;
  public isResendingEmail: boolean;
  public canMarkComplete: boolean;

  get canControl(): boolean {
    return this.permissionService.canControl(this.assessmentSummary.parent);
  }

  get canSendEmail(): boolean {
    return this.canControl || this.permissionService.hasManagerRoles;
  }

  constructor(private router: Router,
              private agilityRiskService: AgilityRiskService,
              private translateService: TranslateService,
              private activatedRoute: ActivatedRoute,
              private dialogService: DialogService,
              private messageService: AgilityRiskMessageService,
              private permissionService: AgilityRiskPermissionService,
              private navigationService: AgilityRiskNavigationService) {
    this.assessmentId = this.activatedRoute.snapshot?.params?.assessmentId;
  }

  ngOnInit(): void {
    this.getRiskAssessmentSummary();
    this.getAdditionalComments();
  }

  getAssessmentSummary() {
    this.agilityRiskService.getAssessmentSummary(this.assessmentId)
      .subscribe(
        (assessmentSummary: AssessmentSummary) => {
          if (this.permissionService.canView(assessmentSummary.parent)) {
            this.assessmentSummary = assessmentSummary;
            this.assessmentSummaryClone = _.cloneDeep(assessmentSummary);
            this.getRiskFormulaBands(this.assessmentSummary.riskFormula.id);
          } else {
            this.router.navigateByUrl(`${this.navigationService.moduleBaseAddress}/dashboard`);
          }
        },
        () => {

        },
        () => {
        }
      );
  }

  getRiskFormulaBands(formulaId: any) {
    this.agilityRiskService.getRiskFormulaBands(formulaId)
      .subscribe((riskBands: Array<RiskBand>) => {
        this.riskBands = riskBands;
        this.initChart();
      });
  }

  getAdditionalComments() {
    this.agilityRiskService.getRiskAssessmentComments(this.assessmentId)
      .subscribe(
        (comments: AssessmentComment[]) => {
          this.additionalComments = comments;
        },
        () => {

        },
        () => {
        }
      );
  }

  getRiskAssessmentSummary() {
    this.agilityRiskService.getRiskAssessmentSummary(this.assessmentId).subscribe({
      next: (res: GetRiskAssessmentSummaryResponse) => {
        this.canMarkComplete = res.canMarkComplete;
        this.riskAssessmentSummaries = _.orderBy(res.riskSummaries, [x => x.name.toLowerCase()], ['asc']);
        this.chartHasData = !!this.riskAssessmentSummaries.find((as: AssessmentSummary) => as.rank > 0);
        this.getAssessmentSummary();
      },
      error: () => {
      }
    });
  }

  defineAnnotations(): void {
    this.riskBands.forEach((rb: RiskBand) => {
      this.annotations.push({
        drawTime: 'beforeDatasetsDraw',
        type: 'line',
        mode: 'vertical',
        scaleID: 'x-axis-0',
        value: rb.numericValue,
        borderColor: '#D3D3D3', // todo: what color?
        label: {
          enabled: true,
          content: rb.name,
          position: 'top',
          backgroundColor: 'rgb(255, 255, 255)',
          fontSize: 14,
          fontColor: 'rgb(0, 0, 0)',
          fontStyle: 'normal'
        }
      });
    });
  }

  initChartData() {
    this.threatsHorizontalChartData = {
      labels: this.riskAssessmentSummaries.map((as: AssessmentSummary) => as.name),
      datasets: [
        {
          label: this.translateService.instant('common.criticality'),
          backgroundColor: '#FFB74D', // TODO: colors....
          data: this.riskAssessmentSummaries.map((as: AssessmentSummary) => as.rank),
          barThickness: 20
        }
      ]
    };

    this.initializingChart = false;
  }

  initChartOptions() {
    this.threatsHorizontalChartOptions = {
      responsive: true,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
          },
          scaleLabel: {
            display: true,
            labelString: this.translateService.instant('common.criticality'),
            fontSize: 16,
            fontStyle: 'bold'
          },
          ticks: {
            min: 0,
            max: this.riskBands[this.riskBands.length - 1]?.numericValue + 5, // give it dead space to the right so annotation label won't get cut off
            stepSize: 1,
            beginAtZero: true,
            display: false
          }
        }],
        yAxes: [{
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
          },
          scaleLabel: {
            fontSize: 16,
            fontStyle: 'bold'
          },
          ticks: {
            padding: 10,
            fontSize: 14
          }
        }]
      },
      annotation: {
        annotations: this.annotations
      },
      onClick: (pointerEvent: PointerEvent, chartElements: Array<any>) => {
        const chartClickedRiskDetailId: any = this.riskAssessmentSummaries[chartElements[0]?._index]?.id;

        if (chartClickedRiskDetailId) {
          this.activeRiskDetailId = chartClickedRiskDetailId;
          this.getRiskAssessmentRisksSummary();
        }
      }
    };
  }

  initChart() {
    this.defineAnnotations();
    this.initChartData();
    this.initChartOptions();
  }

  openAdditionalCommentsModal() {
    const modal = this.dialogService.open(AssessmentDetailAdditionalCommentsModalComponent, {
      data: {
        additionalComments: _.cloneDeep(this.additionalComments), // send a clone over so can cancel,
        canEdit: this.canControl
      },
      header: this.translateService.instant('common.additionalComments'),
      width: '50%'
    });

    modal.onClose.subscribe((additionalComments: AssessmentComment[]) => {
      if (additionalComments) {
        this.additionalComments = additionalComments;

        this.agilityRiskService.putRiskAssessmentComments(this.assessmentId, this.additionalComments)
          .subscribe(
            (baseResponse: BaseResponse) => {
              this.messageService.success(this.translateService.instant('common.saved'));
            },
            err => {

            }
          );
      }
    });
  }

  openMemberAnswersModal(answerTemplate: AnswerTemplate, index: any) {
    const dynamicDialogRef = this.dialogService.open(RiskAssessmentRiskMemberAnswersModalComponent, {
      data: {
        answerTemplate,
        riskDetail: this.activeRiskDetail,
        canEdit: this.canControl
      },
      header: this.translateService.instant('assessment.answerTemplateModalHeader', {number: index + 1}),
      width: '50%'
    });

    dynamicDialogRef.onClose.subscribe(() => {
      // refresh the page data after closing modal so page can reflect changes from modal
      this.getRiskAssessmentRisksSummary();
    });
  }

  orderChart() {
    this.riskAssessmentSummaries = _.orderBy(this.riskAssessmentSummaries, [this.chartSortBy], [this.chartSortBy === this.translateService.instant('common.name').toLowerCase() ? 'asc' : 'desc']);

    this.initChartData();
  }

  markAsReviewed() {
    this.agilityRiskService.putRiskAssessmentStatus(this.assessmentId, this.assessmentStatus.current).subscribe(
      () => {
        this.messageService.success(this.translateService.instant('common.saved'));

        this.assessmentSummary.status = this.assessmentStatus.current;
      },
      () => {
      }
    );
  }

  getRiskAssessmentRisksSummary() {
    this.agilityRiskService.getRiskAssessmentRisksSummary(this.activeRiskDetailId)
      .subscribe((res: RiskDetail) => {
        this.activeRiskDetail = res;
      });
  }

  titleCase(string: string) {
    if (!string || !string?.length) {
      return '';
    }

    return string[0]?.toUpperCase() + string.substr(1).toLowerCase();
  }

  openCustomFieldsModal() {
    const customFieldsModalRef: DynamicDialogRef = this.dialogService.open(RiskAssessmentDetailsCustomFieldsModalComponent, {
      data: {
        id: this.activeRiskDetailId,
        canEdit: this.canControl
      },
      header: this.translateService.instant('assessment.survey.customFields'),
      width: '50%'
    });

    customFieldsModalRef.onClose.subscribe(() => customFieldsModalRef.destroy());
  }

  saveAssessmentName(): void {
    this.agilityRiskService.putRiskAssessment(this.assessmentSummary)
      .subscribe(
        () => {
          this.messageService.success(this.translateService.instant('common.saved'));
          this.assessmentSummaryClone.name = this.assessmentSummary.name;
          this.isEditingAssessmentName = false;
        },
        () => {
          this.messageService.error(this.translateService.instant('common.error'));
          this.isEditingAssessmentName = false;
        }
      );
  }

  cancelEditAssessmentName(): void {
    this.assessmentSummary.name = this.assessmentSummaryClone.name;
    this.isEditingAssessmentName = false;
  }

  resendEmail(assessmentDistributionMemberData?: AssessmentDistributionMemberData): void {
    if (assessmentDistributionMemberData?.riskCompleted) {
      this.messageService.info(this.translateService.instant('assessment.recipientCompletedSurvey'));
      return;
    }

    this.isResendingEmail = true;

    this.agilityRiskService.postRiskAssessmentEmail(this.assessmentId, !assessmentDistributionMemberData, assessmentDistributionMemberData?.member?.id)
      .subscribe(
        () => {
          this.messageService.success(this.translateService.instant('assessment.resendEmailAllSuccess'));
          this.assessmentSummaryClone.name = this.assessmentSummary.name;
          this.isResendingEmail = false;
        },
        () => {
          this.isResendingEmail = false;
          this.messageService.error(this.translateService.instant('common.error'));
        }
      );
  }

  canMarkAsReviewed(): boolean {
    return (this.assessmentSummary?.percentInt == 100 && this.assessmentSummary?.status.toLowerCase() === this.assessmentStatus.incomplete.toLowerCase()) ||
      (this.canMarkComplete);
  }

  memberCanSendEmail(member: AssessmentDistributionMemberData): boolean {
    return (this.canControl || this.permissionService.hasManagerRoles) && !member.deletedPersonnelRecord;
  }
}
